import {
  ConditionOperatorEnumType,
  CountOperator,
  MAX_RELATED_RESOURCE_DEPTH,
} from '@reach/shared-types/conditions';
import { generateId } from '../utils/generate-id';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import React from 'react';
import { AddDescriptionButton } from './AddDescriptionButton';
import { AddRelatedResourceCondition } from './AddRelatedResourceCondition';
import { BuilderTooltip } from './BuilderTooltip';
import { ClauseDescriptionHeadline } from './ClauseDescriptionHeadline';
import { FieldConditionRow } from './FieldConditionRow';
import { SchemaNameChip } from './SchemaNameChip';
import { ambiguousRelatedSchemaIds, schemaTintStyle } from './schema-theming';
import { buildAddAttributeLabel } from './segment-builder.utils';
import { useSegmentSchemaCatalog } from './SegmentSchemaCatalog';
import type {
  BuilderFieldCondition,
  BuilderRelatedResourceCondition,
  FieldConditionSubject,
} from './segment-builder.types';
import { isBuilderRelatedResourceCondition } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';
import { useSegmentReadOnly } from './segment-read-only-context';

const COUNT_OPERATOR_OPTIONS: { value: CountOperator; label: string }[] = [
  { value: CountOperator.EQ, label: 'exactly' },
  { value: CountOperator.GT, label: 'more than' },
  { value: CountOperator.GTE, label: 'at least' },
  { value: CountOperator.LT, label: 'fewer than' },
  { value: CountOperator.LTE, label: 'at most' },
];

interface RelatedResourceBlockProps {
  condition: BuilderRelatedResourceCondition;
  /** Sentence lead before the subject chip, e.g. "The" or "and that". */
  subjectLead?: string;
  subject?: FieldConditionSubject;
  /** The record this block hangs off — decides whether a "via {fk}" label is needed to disambiguate. */
  subjectSchemaId?: string | null;
  depth?: number;
  onUpdate: (condition: BuilderRelatedResourceCondition) => void;
  onDelete: () => void;
  disabled?: boolean;
}

const FALLBACK_SUBJECT: FieldConditionSubject = { name: 'Contact' };

export const RelatedResourceBlock: React.FC<RelatedResourceBlockProps> = ({
  condition,
  subjectLead = 'The',
  subject = FALLBACK_SUBJECT,
  subjectSchemaId,
  depth = 1,
  onUpdate,
  onDelete,
  disabled = false,
}) => {
  const readOnly = useSegmentReadOnly();
  const canAddDescription = !readOnly && condition.description === undefined;
  const { getSchemaById, schemaCatalog } = useSegmentSchemaCatalog();
  if (!schemaCatalog) return null;

  const schema = getSchemaById(condition.schemaId);
  const fieldOptions = schema?.fields ?? [];
  const showViaLabel = ambiguousRelatedSchemaIds(schemaCatalog, subjectSchemaId).has(condition.schemaId);
  const isExpanded = condition.isExpanded;
  const editingDisabled = disabled || condition.unavailableReason !== undefined;
  const canNestDeeper = depth < MAX_RELATED_RESOURCE_DEPTH;
  const addConditionLabel = buildAddAttributeLabel(condition.schemaName);
  const relatedSubject = {
    schemaId: condition.schemaId,
    name: condition.schemaName,
    displayMetadata: schema?.displayMetadata,
  };
  const viaLabel = `Linked via ${condition.foreignKeyPath}`;
  const hasNestedConditions = condition.conditions.length > 0;
  const objectChip = <SchemaNameChip subject={relatedSubject} tabIndex={showViaLabel ? 0 : undefined} />;

  const toggleExpanded = () => {
    onUpdate({ ...condition, isExpanded: !condition.isExpanded });
  };

  const handleCountOperatorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onUpdate({
      ...condition,
      countOperator: e.target.value as CountOperator,
    });
  };

  const handleCountValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = parseInt(e.target.value, 10);
    if (!Number.isNaN(v) && v >= 0) {
      onUpdate({ ...condition, countValue: v });
    }
  };

  const handleNestedConditionUpdate = (
    index: number,
    updated: BuilderFieldCondition | BuilderRelatedResourceCondition,
  ) => {
    onUpdate({ ...condition, conditions: condition.conditions.with(index, updated) });
  };

  const handleNestedConditionDelete = (index: number) => {
    onUpdate({ ...condition, conditions: condition.conditions.toSpliced(index, 1) });
  };

  const handleDescriptionChange = (description: string | undefined) => {
    onUpdate({ ...condition, description });
  };

  const handleAddNestedCondition = () => {
    const newCond: BuilderFieldCondition = {
      id: generateId(),
      type: 'field',
      field: fieldOptions[0]?.name ?? '',
      fieldType: fieldOptions[0]?.type ?? 'unknown',
      operator: ConditionOperatorEnumType.EQUALS,
      value: [],
    };
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  const handleAddNestedRelatedResource = (newCond: BuilderRelatedResourceCondition) => {
    onUpdate({
      ...condition,
      conditions: [...condition.conditions, newCond],
    });
  };

  return (
    <div className={styles.relatedBlock} style={schemaTintStyle(schema?.displayMetadata)}>
      <div className={styles.relatedSentence}>
        <button
          type="button"
          className={styles.relatedTitleButton}
          onClick={toggleExpanded}
          disabled={disabled}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Collapse' : 'Expand'}>
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        <span className={styles.subjectPhrase}>{subjectLead}</span>
        <SchemaNameChip subject={subject} />
        <span className={styles.subjectPhrase}>has</span>
        <select
          className={styles.selectInput}
          value={condition.countOperator}
          onChange={handleCountOperatorChange}
          disabled={editingDisabled}
          style={{ width: 'auto' }}
          aria-label="Count operator">
          {COUNT_OPERATOR_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          type="number"
          min={0}
          value={condition.countValue}
          onChange={handleCountValueChange}
          disabled={editingDisabled}
          className={styles.textInput}
          style={{ width: '3.5rem' }}
          aria-label="Count value"
        />
        {showViaLabel ? <BuilderTooltip label={viaLabel}>{objectChip}</BuilderTooltip> : objectChip}
        {hasNestedConditions && <span className={styles.wherePhrase}>where:</span>}
        <span className={styles.relatedControls}>
          {canAddDescription && (
            <AddDescriptionButton
              onClick={() => handleDescriptionChange('')}
              disabled={editingDisabled}
            />
          )}
          {!readOnly && (
            <button
              type="button"
              className={styles.relatedTitleButton}
              onClick={onDelete}
              disabled={disabled}
              aria-label="Remove related resource condition">
              <Trash2 size={15} />
            </button>
          )}
        </span>
      </div>

      {condition.unavailableReason && (
        <p className={styles.relatedUnresolvableNote}>{condition.unavailableReason}</p>
      )}

      {condition.description !== undefined && (
        <div className={styles.relatedDescription}>
          <ClauseDescriptionHeadline
            variant="row"
            description={condition.description}
            onChange={handleDescriptionChange}
            disabled={editingDisabled}
          />
        </div>
      )}

      {isExpanded && (
        <div className={styles.relatedNested}>
          {condition.conditions.map((c, i) => (
            <React.Fragment key={c.id}>
              {i > 0 && (
                <div className={styles.staticConnector}>
                  <span className={styles.staticConnectorLabel}>AND</span>
                  <span className={styles.andConnectorLine} />
                </div>
              )}
              {isBuilderRelatedResourceCondition(c) ? (
                <RelatedResourceBlock
                  condition={c}
                  subjectLead="and that"
                  subject={relatedSubject}
                  subjectSchemaId={condition.schemaId}
                  depth={depth + 1}
                  onUpdate={handleNestedConditionUpdate.bind(null, i)}
                  onDelete={handleNestedConditionDelete.bind(null, i)}
                  disabled={editingDisabled}
                />
              ) : (
                <FieldConditionRow
                  condition={c}
                  fieldOptions={fieldOptions}
                  subject={relatedSubject}
                  onUpdate={handleNestedConditionUpdate.bind(null, i)}
                  onDelete={handleNestedConditionDelete.bind(null, i)}
                  disabled={editingDisabled}
                />
              )}
            </React.Fragment>
          ))}
          {!readOnly && (
            <div className={styles.addConditionRow}>
              <button
                type="button"
                className={styles.addConditionBtn}
                onClick={handleAddNestedCondition}
                disabled={editingDisabled || fieldOptions.length === 0}>
                <Plus size={14} />
                {addConditionLabel}
              </button>
              {canNestDeeper && (
                <AddRelatedResourceCondition
                  subjectSchemaId={condition.schemaId}
                  addRelatedResourceCondition={handleAddNestedRelatedResource}
                  disabled={editingDisabled}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
